import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '.';
import { filterAndSortRepairOrders, getRepairOrderByRepairOrderNumber, getRepairOrderSummaryByKeyword } from '../api/dms/repairOrder.api';
import { logConsole, logTransaction } from '../common/log';
import { handleThunkError } from '../common/util';
import { IRepairOrder, IRepairOrderSort, mapRepairOrderDetailToRepairOrderWithJobs, mapRepairOrderSummaryToRepairOrderWithJobs } from '../models/dms/repairOrder.model';
import { ITechComment } from '../models/dms/technicianComments.model';
import { LogType } from '../models/log.model';
import { ConfigName, updateConfigObject } from '../sqlite/common/config';
import { addCustomRepairOrderToRepairOrderSyncStoreAsync } from './repairOrderSyncSlice';
import { Moment } from 'moment';

interface IRepairOrderState {
  repairOrders?: IRepairOrder[];
  selectedRepairOrder?: IRepairOrder;
  selectedTechComments?: ITechComment[];
  repairOrderSearchLoading: boolean;
}

const initialState: IRepairOrderState = {
  repairOrders: [] as IRepairOrder[],
  selectedRepairOrder: {} as IRepairOrder,
  selectedTechComments: [] as ITechComment[],
  repairOrderSearchLoading: false,
};

/// THUNKS

export const setRepairOrdersFromSyncByKeywordAsync = createAsyncThunk<{}, { keyword?: string; branchCode?: string; techCode?: string; startDateTime?: Moment; endDateTime?: Moment; extended?: boolean; orderBy?: IRepairOrderSort }>(
  'repairOrder/setRepairOrdersFromSyncByKeywordStoreAsync',
  async (payload, { rejectWithValue, dispatch, getState }) => {
    logTransaction(LogType.Info, `Setting repair orders from sync by keyword '${payload.keyword}' and branch '${payload.branchCode}' and techCode '${payload.techCode}' to storage`, '[repairOrder] setRepairOrdersFromSyncByKeywordStoreAsync');
    try {
      let roWithJobs: IRepairOrder | undefined;

      const repairOrdersFromState = (getState() as RootState).repairOrderSync.repairOrderSync?.data || [];
      let filteredRepairOrders = [...filterAndSortRepairOrders(repairOrdersFromState, payload.keyword, payload.branchCode, payload.techCode, payload.startDateTime, payload.endDateTime, payload.orderBy)];

      dispatch(setRepairOrdersState(filteredRepairOrders));

      // Perform an RO search and add this to the result if found
      const roNumber = parseInt(payload.keyword || '') || undefined;
      if (roNumber) {
        try {
          dispatch(setRepairOrderSearchLoadingState(true));
          const roSearch = await getRepairOrderByRepairOrderNumber(roNumber);
          roWithJobs = mapRepairOrderDetailToRepairOrderWithJobs(roSearch);

          if (roWithJobs) {
            // Filter by branch, techCode and keyword
            const filteredRoWithJobs = filterAndSortRepairOrders([roWithJobs], payload.keyword, payload.branchCode, payload.techCode, payload.startDateTime, payload.endDateTime, payload.orderBy);
            if (filteredRoWithJobs.length) {
              roWithJobs = filteredRoWithJobs[0];
              // If the RO that was retrieved is already in the cache, ensure it is not duplicated by removing it from the cache first before refreshing the cache
              const cachedMatchOfRoSearchIndex = filteredRepairOrders.findIndex((ro) => ro.repairOrderNumber === roWithJobs?.repairOrderNumber && !ro.isAdhoc);
              if (cachedMatchOfRoSearchIndex >= 0) {
                filteredRepairOrders.splice(cachedMatchOfRoSearchIndex, 1);
              }

              filteredRepairOrders = [roWithJobs, ...filteredRepairOrders];
              dispatch(setRepairOrdersState(filteredRepairOrders));
            }
          }
          dispatch(setRepairOrderSearchLoadingState(false));
        } catch (err) {
          return handleThunkError(err, rejectWithValue, undefined, setRepairOrderSearchLoadingState(false));
        }
      }

      // Search for ROs by keyword (slow)
      if (payload.extended && payload.keyword) {
        try {
          dispatch(setRepairOrderSearchLoadingState(true));
          const roSearchExtended = await getRepairOrderSummaryByKeyword(payload.keyword);
          const newRos: IRepairOrder[] = [];
          roSearchExtended.items.forEach((roExtended) => {
            // If the RO that was retrieved is already in the cache, ensure it is not duplicated by removing it from the cache first before refreshing the cache
            const cachedMatchOfRoSearchIndex = filteredRepairOrders.findIndex((ro) => ro.repairOrderNumber === roExtended?.repairOrderNumber && !ro.isAdhoc);
            if (cachedMatchOfRoSearchIndex >= 0) {
              filteredRepairOrders.splice(cachedMatchOfRoSearchIndex, 1);
            }
            const roWithJobs = mapRepairOrderSummaryToRepairOrderWithJobs(roExtended);
            if (roWithJobs) {
              newRos.push(roWithJobs);
            }
          });
          dispatch(setRepairOrdersState([...newRos, ...filteredRepairOrders]));
          dispatch(setRepairOrderSearchLoadingState(false));
        } catch (err) {
          return handleThunkError(err, rejectWithValue, undefined, setRepairOrderSearchLoadingState(false));
        }
      }
    } catch (err: any) {
      return handleThunkError(err, rejectWithValue);
    }
  },
);

export const setSelectedRepairOrderStoreAsync = createAsyncThunk<{}, IRepairOrder>('repairOrder/setSelectedRepairOrderStoreAsync', async (repairOrder, { rejectWithValue, dispatch, getState }) => {
  logTransaction(LogType.Info, `Setting selected repair order with RO number '${repairOrder.repairOrderNumber}' to storage`, '[repairOrder] setSelectedRepairOrderStoreAsync');
  try {
    dispatch(setSelectedRepairOrderState(repairOrder));
    await updateConfigObject<IRepairOrder>(ConfigName.SelectedRepairOrder, repairOrder, false);

    // If RO is from detail (custom RO search), add it to the store
    if (repairOrder.fromDetail) {
      await dispatch(addCustomRepairOrderToRepairOrderSyncStoreAsync(repairOrder));
    }
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue);
  }
});

/// SLICE

export const repairOrderSlice = createSlice({
  name: 'repairOrder',
  initialState,
  reducers: {
    setRepairOrdersState: (state, action: PayloadAction<IRepairOrder[]>) => {
      logConsole(LogType.Info, '[repairOrder] setRepairOrdersState');
      state.repairOrders = action.payload;
    },
    setSelectedRepairOrderState: (state, action: PayloadAction<IRepairOrder>) => {
      logConsole(LogType.Info, '[repairOrder] setSelectedRepairOrderState');
      state.selectedRepairOrder = action.payload;
    },
    setRepairOrderSearchLoadingState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[repairOrder] setRepairOrderSearchLoadingState: ${action.payload}`);
      state.repairOrderSearchLoading = action.payload;
    },
  },
});

/// REDUCERS

export const { setRepairOrdersState, setSelectedRepairOrderState, setRepairOrderSearchLoadingState } = repairOrderSlice.actions;

export default repairOrderSlice.reducer;
